import { convertObjToQueryStr } from "@utils/utils"
import { api } from "./api"
import store from "@/store"
import _ from "lodash"
import { useLangRefetch } from "./utils"
import type { components, JsonResponse, paths, WithId } from "./generated"

export type Group = WithId<components["schemas"]["Group"]>
type GrowthStat = JsonResponse<paths["/api/statistics/growthStat/{groupId}"]["get"]>
type RootGroup = WithId<components["schemas"]["RootGroup"]>

type PopulatedProfile = WithId<components["schemas"]["ProfilePublic"]>
type PopulatedGroupMember = Omit<components["schemas"]["GroupMember"], "profile"> & { profile: PopulatedProfile }
// Only `/group/{id}` always returns these references populated. `/group/search` and `/group/own`
// populate `members.profile` only when the `populateMembers` query param is set; otherwise (and for
// the plain list endpoint) they return bare ObjectIds, so they are typed as the base `Group`.
type PopulatedGroup = WithId<
	Omit<components["schemas"]["Group"], "root" | "creator" | "members"> & {
		root: RootGroup
		creator?: PopulatedProfile
		members?: PopulatedGroupMember[]
	}
>
type Confirmation = WithId<components["schemas"]["Confirmation"]>
type InvitedProfile = { _id: string }

type Paginated<T> = {
	docs: T[]
	totalDocs?: number
	totalPages?: number
	page?: number
	limit?: number
	prevPage?: number | null
	nextPage?: number | null
	hasPrevPage?: boolean
	hasNextPage?: boolean
	pagingCounter?: number
}

type GroupListResponse = { groups?: Group[]; rootGroups?: RootGroup[] }
type MemberBody = { profileIds?: string[]; groupIds?: string[]; name?: string; roleName?: string }

const groupApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getGroupById: builder.query<PopulatedGroup, { id: string }>({
			query: ({ id: groupId }) => `/group/${groupId}`,
			providesTags: (_result, _error, arg) => [{ type: "Group" as const, id: arg.id }, "Loc" as const],
		}),
		getGroups: builder.query<GroupListResponse, Record<string, unknown> | void>({
			query: (queryObj) => `/group${convertObjToQueryStr(queryObj)}`,
			providesTags: (result, error) => {
				if (error || !result) return []
				return [
					...(result.groups ?? []).map((doc) => ({ type: "Group" as const, id: doc._id })),
					{ type: "Group" as const, id: "LIST" },
					...(result.rootGroups ?? []).map((doc) => ({ type: "RootGroup" as const, id: doc._id })),
					{ type: "RootGroup" as const, id: "LIST" },
				]
			},
		}),
		searchGroups: builder.query<Paginated<Group>, { query?: Record<string, unknown> } | Record<string, unknown>>({
			query: (queryObj) => `/group/search${convertObjToQueryStr(queryObj.query ?? queryObj)}`,
			providesTags: () => ["Group"],
		}),
		searchGroupMembers: builder.query<
			Paginated<Record<string, any>>,
			{ query?: Record<string, unknown> } | Record<string, unknown>
		>({
			query: (queryObj) => `/group/searchMembers${convertObjToQueryStr(queryObj.query ?? queryObj)}`,
			providesTags: () => ["Group"],
		}),
		getGroupHierarchy: builder.query<unknown, void>({
			query: () => "/group/hierarchy",
		}),
		// `/group/own` returns a bare `Group[]` by default, or `{ groups, rootGroups }` when the
		// `root=true` query param is set (swagger `oneOf`).
		getMyGroups: builder.query<GroupListResponse | Group[], Record<string, unknown> | void>({
			query: (queryObj) => `/group/own${convertObjToQueryStr(queryObj)}`,
			providesTags: (result, error) => {
				if (error || !result) return []
				if (Array.isArray(result)) {
					return [...result.map((doc) => ({ type: "Group" as const, id: doc._id })), { type: "Group" as const, id: "LIST" }]
				}
				return [
					...(result.groups ?? []).map((doc) => ({ type: "Group" as const, id: doc._id })),
					{ type: "Group" as const, id: "LIST" },
					...(result.rootGroups ?? []).map((doc) => ({ type: "RootGroup" as const, id: doc._id })),
					{ type: "RootGroup" as const, id: "LIST" },
				]
			},
		}),

		createGroup: builder.mutation<Group, FormData | (Partial<components["schemas"]["Group"]> & Record<string, unknown>)>({
			query: (body) => ({
				url: "/group",
				method: "POST",
				body,
			}),
			invalidatesTags: (result) => [
				{ type: "Group", id: "LIST" },
				{ type: "RootGroup", id: result?.root },
				{ type: "Profile", id: store.getState().user.profile },
			],
		}),
		updateGroup: builder.mutation<Group, { groupId: string; body: FormData | Partial<components["schemas"]["Group"]> }>({
			query: ({ groupId, body }) => ({
				url: `/group/${groupId}`,
				method: "PUT",
				body,
			}),
			invalidatesTags: (result, _error, arg) => [
				{ type: "Group", id: arg.groupId },
				{ type: "RootGroup", id: result?.root },
			],
		}),
		deleteGroup: builder.mutation<void, string>({
			query: (groupId) => ({
				url: `/group/${groupId}`,
				method: "DELETE",
			}),
			invalidatesTags: (_result, _error, arg) => [{ type: "Group", id: arg }],
		}),
		deleteGroupWithoutInvalidation: builder.mutation<void, string>({
			query: (groupId) => ({
				url: `/group/${groupId}`,
				method: "DELETE",
			}),
		}),
		addGroupRole: builder.mutation<Group, { groupId: string; body: unknown }>({
			query: ({ groupId, body }) => ({
				url: `/group/${groupId}/role`,
				method: "POST",
				body,
			}),
			invalidatesTags: (_result, _error, arg) => [
				{ type: "Group", id: arg.groupId },
				{ type: "RootGroup", id: arg.groupId },
			],
		}),
		updateGroupRole: builder.mutation<Group, { groupId: string; body: unknown }>({
			query: ({ groupId, body }) => ({
				url: `/group/${groupId}/role`,
				method: "PUT",
				body,
			}),
			invalidatesTags: (_result, _error, arg) => [
				{ type: "Group", id: arg.groupId },
				{ type: "RootGroup", id: arg.groupId },
			],
		}),
		deleteGroupRole: builder.mutation<Group, { groupId: string; targetName: string }>({
			query: ({ groupId, targetName }) => ({
				url: `/group/${groupId}/role`,
				method: "DELETE",
				body: { targetName },
			}),
			invalidatesTags: (_result, _error, arg) => [
				{ type: "Group", id: arg.groupId },
				{ type: "RootGroup", id: arg.groupId },
			],
		}),
		addGroupMemberMulti: builder.mutation<Group[], { body: MemberBody }>({
			query: ({ body }) => ({
				url: "/group/member",
				method: "POST",
				body,
			}),
			invalidatesTags: (result, _error, arg) => {
				const profileIds = arg.body?.profileIds ?? []
				const groupIds = arg.body?.groupIds ?? []
				const rootGroupIds = _.flatMap(result, "root").map((rg) => rg._id ?? rg)

				return [
					...rootGroupIds.map((rgId) => ({ type: "RootGroup" as const, id: rgId })),
					...groupIds.map((gId) => ({ type: "Group" as const, id: gId })),
					...profileIds.map((pId) => ({ type: "Profile" as const, id: pId })),
					"RootGroup" as const,
					"Group" as const,
				]
			},
		}),
		deleteGroupMemberMulti: builder.mutation<Group[], { body: MemberBody }>({
			query: ({ body }) => ({
				url: "/group/member",
				method: "DELETE",
				body,
			}),
			invalidatesTags: (result, _error, arg) => {
				const profileIds = arg.body?.profileIds ?? []
				const groupIds = arg.body?.groupIds ?? []
				const rootGroupIds = _.flatMap(result, "root").map((rg) => rg._id ?? rg)

				return [
					...rootGroupIds.map((rgId) => ({ type: "RootGroup" as const, id: rgId })),
					...groupIds.map((gId) => ({ type: "Group" as const, id: gId })),
					...profileIds.map((pId) => ({ type: "Profile" as const, id: pId })),
				]
			},
		}),
		addGroupMember: builder.mutation<Group, { groupId: string; rootId?: string; body: unknown }>({
			query: ({ groupId, body }) => ({
				url: `/group/${groupId}/member`,
				method: "POST",
				body,
			}),
			invalidatesTags: (_result, _error, arg) => [
				{ type: "Group", id: arg.groupId },
				{ type: "RootGroup", id: arg.rootId },
			],
		}),
		editGroupMember: builder.mutation<Group, { groupId: string; profileId: string; body: unknown }>({
			query: ({ groupId, profileId, body }) => ({
				url: `/group/${groupId}/member/${profileId}`,
				method: "PUT",
				body,
			}),
			invalidatesTags: (_result, _error, arg) => [
				{ type: "Group", id: arg.groupId },
				{ type: "RootGroup", id: arg.groupId },
			],
		}),
		deleteGroupMember: builder.mutation<Group, { groupId: string; profileId: string; body?: unknown }>({
			query: ({ groupId, profileId, body }) => ({
				url: `/group/${groupId}/member/${profileId}`,
				method: "DELETE",
				body,
			}),
			invalidatesTags: (_result, _error, arg) => [
				{ type: "Group", id: arg.groupId },
				{ type: "RootGroup", id: arg.groupId },
				{ type: "Profile", id: arg.profileId },
			],
		}),
		deleteGroupMembers: builder.mutation<Group, { groupId: string; body: { profileIds: string[] } }>({
			query: ({ groupId, body }) => ({
				url: `/group/${groupId}/member`,
				method: "DELETE",
				body,
			}),
			invalidatesTags: (result, _error, arg) => [
				{ type: "Group" as const, id: arg.groupId },
				{ type: "RootGroup" as const, id: result?.root },
				...arg.body.profileIds.map((pId) => ({ type: "Profile" as const, id: pId })),
			],
		}),

		inviteToGroupMultiple: builder.mutation<InvitedProfile[], { body: MemberBody; rootIds?: string[] }>({
			query: ({ body }) => ({
				url: "/group/invite",
				method: "POST",
				body,
			}),
			invalidatesTags: (_result, _error, arg) => {
				const profileIds = arg.body?.profileIds ?? []
				const groupIds = arg.body?.groupIds ?? []
				const rootGroupIds = arg.rootIds ?? []

				return [
					...rootGroupIds.map((rgId) => ({ type: "RootGroup" as const, id: rgId })),
					...groupIds.map((gId) => ({ type: "Group" as const, id: gId })),
					...profileIds.map((pId) => ({ type: "Profile" as const, id: pId })),
					"RootGroup" as const,
					"Group" as const,
				]
			},
		}),
		inviteToGroup: builder.mutation<InvitedProfile[], { groupId: string; rootId?: string; body: unknown }>({
			query: ({ groupId, body }) => ({
				url: `/group/${groupId}/invite`,
				method: "POST",
				body,
			}),
			invalidatesTags: (result, _error, arg) => [
				{ type: "Group" as const, id: arg.groupId },
				{ type: "RootGroup" as const, id: arg.rootId },
				...(result ?? []).map((p) => ({ type: "Profile" as const, id: p._id })),
			],
		}),
		cancelGroupInvite: builder.mutation<Group, { groupId: string; inviteId: string; rootId?: string; profile?: string }>({
			query: ({ groupId, inviteId }) => ({
				url: `/group/${groupId}/cancelInvite/${inviteId}`,
				method: "POST",
			}),
			invalidatesTags: (_result, _error, arg) =>
				_.compact([
					arg.rootId && { type: "RootGroup" as const, id: arg.rootId },
					{ type: "Group" as const, id: arg.groupId },
					{ type: "RootGroup" as const, id: arg.groupId },
					arg.profile && { type: "Profile" as const, id: arg.profile },
				]),
		}),

		acceptGroupInvite: builder.mutation<{ message?: string }, { groupId: string; bulletinId: string }>({
			query: ({ groupId, bulletinId }) => ({
				url: `/group/${groupId}/acceptInvite/${bulletinId}`,
				method: "POST",
			}),
			invalidatesTags: (_result, _error, arg) => [
				{ type: "Group", id: arg.groupId },
				{ type: "RootGroup", id: arg.groupId },
				{ type: "Profile", id: store.getState().user.profile },
			],
		}),
		declineGroupInvite: builder.mutation<{ message?: string }, { groupId: string; bulletinId: string }>({
			query: ({ groupId, bulletinId }) => ({
				url: `/group/${groupId}/declineInvite/${bulletinId}`,
				method: "POST",
			}),
			invalidatesTags: (_result, _error, arg) => [
				{ type: "Group", id: arg.groupId },
				{ type: "RootGroup", id: arg.groupId },
				{ type: "Profile", id: store.getState().user.profile },
			],
		}),
		cancelConfirmation: builder.mutation<Confirmation, { groupId: string; confirmationId: string; rootId?: string }>({
			query: ({ groupId, confirmationId }) => ({
				url: `/group/${groupId}/cancelConfirmation/${confirmationId}`,
				method: "PUT",
			}),
			invalidatesTags: (_result, _error, arg) =>
				_.compact([
					arg.rootId && { type: "RootGroup" as const, id: arg.rootId },
					{ type: "Group" as const, id: arg.groupId },
					{ type: "RootGroup" as const, id: arg.groupId },
				]),
		}),
		getGrowthStat: builder.query<GrowthStat, string>({
			query: (groupId) => `/statistics/growthStat/${groupId}`,
			providesTags: () => ["TrainingResult", "Event"],
		}),
		leaveAddedGroup: builder.mutation<Group, { groupId: string; bulletinId: string; body: unknown }>({
			query: ({ groupId, bulletinId, body }) => ({
				url: `/group/${groupId}/leaveAddedGroup/${bulletinId}`,
				method: "POST",
				body: body,
			}),
			invalidatesTags: (_result, _error, arg) => [
				{ type: "Group", id: arg.groupId },
				{ type: "RootGroup", id: arg.groupId },
				{ type: "Profile", id: store.getState().user.profile },
			],
		}),
		membershipRemovalRequest: builder.mutation<unknown[], { groupId: string; groupRole: string }>({
			query: ({ groupId, groupRole }) => ({
				url: `/group/${groupId}/requestRemoval`,
				method: "POST",
				body: { groupRole },
			}),
			invalidatesTags: () => ["Profile"],
		}),
		membershipRemovalCancel: builder.mutation<unknown[], { groupId: string; bulletinId: string }>({
			query: ({ groupId, bulletinId }) => ({
				url: `/group/${groupId}/requestRemoval/${bulletinId}/cancel`,
				method: "POST",
			}),
			invalidatesTags: () => ["Profile"],
		}),
		membershipRemovalDecline: builder.mutation<string, { groupId: string; bulletinId: string }>({
			query: ({ groupId, bulletinId }) => ({
				url: `/group/${groupId}/requestRemoval/${bulletinId}/decline`,
				method: "POST",
				responseHandler: (response) => {
					if (response.headers.get("content-type")?.includes("text")) {
						return response.text()
					}
					return response.json()
				},
			}),
			invalidatesTags: () => ["Profile"],
		}),
		membershipRemovalAccept: builder.mutation<string, { groupId: string; bulletinId: string }>({
			query: ({ groupId, bulletinId }) => ({
				url: `/group/${groupId}/requestRemoval/${bulletinId}/accept`,
				method: "POST",
				responseHandler: (response) => {
					if (response.headers.get("content-type")?.includes("text")) {
						return response.text()
					}
					return response.json()
				},
			}),
			invalidatesTags: (_result, _error, arg) => ["Profile", { type: "Group", id: arg.groupId }],
		}),

		setPassiveGroup: builder.mutation<void, { groupId: string }>({
			query: ({ groupId }) => ({
				url: `/group/${groupId}/setPassive`,
				method: "POST",
			}),
			invalidatesTags: () => ["Group", "Profile", "RootGroup"],
		}),
		unsetPassiveGroup: builder.mutation<void, { groupId: string }>({
			query: ({ groupId }) => ({
				url: `/group/${groupId}/unsetPassive`,
				method: "POST",
			}),
			invalidatesTags: () => ["Group", "Profile", "RootGroup"],
		}),
	}),
})

export const {
	useGetGroupsQuery,
	useGetMyGroupsQuery,
	useGetGroupByIdQuery,
	useCreateGroupMutation,
	useUpdateGroupMutation,
	useAddGroupMemberMultiMutation,
	useDeleteGroupMemberMultiMutation,
	useAddGroupMemberMutation,
	useDeleteGroupMemberMutation,
	useInviteToGroupMultipleMutation,
	useInviteToGroupMutation,
	useCancelGroupInviteMutation,
	useAcceptGroupInviteMutation,
	useDeclineGroupInviteMutation,
	useCancelConfirmationMutation,
	useGetGrowthStatQuery,
	useSearchGroupsQuery,
	useSearchGroupMembersQuery,
	useLeaveAddedGroupMutation,
	useMembershipRemovalRequestMutation,
	useMembershipRemovalCancelMutation,
	useMembershipRemovalAcceptMutation,
	useMembershipRemovalDeclineMutation,
	useSetPassiveGroupMutation,
	useUnsetPassiveGroupMutation,
} = groupApi

export const useGetGroupByIdQueryL = ((arg, options) =>
	useLangRefetch(useGetGroupByIdQuery, arg, options)) as typeof useGetGroupByIdQuery
